import { type CSSProperties } from 'react';
import { type DayLayoutFunction } from 'react-big-calendar';

import { type CalendarEvent } from './types';

interface DenseLayoutProxy {
  container?: DenseLayoutProxy;
  data: CalendarEvent;
  end: number;
  endMs: number;
  height: number;
  leaves?: DenseLayoutProxy[];
  row?: DenseLayoutProxy;
  rows?: DenseLayoutProxy[];
  start: number;
  startMs: number;
  top: number;
}

type DenseEventStyle = CSSProperties & {
  xOffset: number;
};

const STAGGER_COMPRESSION = 0.22;

const getDenseProxyBaseWidth = (event: DenseLayoutProxy): number => {
  if (event.rows) {
    const columns = event.rows.reduce(
      (max, row) => Math.max(max, (row.leaves?.length ?? 0) + 1),
      0,
    );
    return 100 / (columns + 1);
  }

  if (event.leaves && event.container) {
    return (100 - getDenseProxyBaseWidth(event.container)) / (event.leaves.length + 1);
  }

  return event.row ? getDenseProxyBaseWidth(event.row) : 100;
};

const getDenseProxyXOffset = (event: DenseLayoutProxy): number => {
  if (event.rows) return 0;
  if (event.leaves && event.container) return getDenseProxyBaseWidth(event.container);
  if (!event.row) return 0;

  const leaves = event.row.leaves ?? [];
  return (
    getDenseProxyXOffset(event.row) +
    (leaves.indexOf(event) + 1) * getDenseProxyBaseWidth(event.row)
  );
};

const areDenseEventsOnSameRow = (
  a: DenseLayoutProxy,
  b: DenseLayoutProxy,
  minimumStartDifference: number,
) =>
  Math.abs(b.start - a.start) < minimumStartDifference ||
  (b.start > a.start && b.start < a.end);

const sortDenseEventsForRender = (events: DenseLayoutProxy[]) => {
  const sortedByTime = [...events].sort((a, b) => a.startMs - b.startMs || b.endMs - a.endMs);
  const sorted: DenseLayoutProxy[] = [];

  while (sortedByTime.length > 0) {
    const event = sortedByTime.shift();
    if (!event) continue;
    sorted.push(event);

    for (let i = 0; i < sortedByTime.length; i += 1) {
      const candidate = sortedByTime[i];
      if (event.endMs > candidate.startMs) continue;

      if (i > 0) {
        const nextEvent = sortedByTime.splice(i, 1)[0];
        sorted.push(nextEvent);
      }
      break;
    }
  }

  return sorted;
};

export const denseOverlapLayout: DayLayoutFunction<CalendarEvent> = ({
  events,
  minimumStartDifference,
  slotMetrics,
  accessors,
}) => {
  const proxies: DenseLayoutProxy[] = events.map(event => {
    const range = slotMetrics.getRange(accessors.start(event), accessors.end(event));
    return {
      data: event,
      end: range.end,
      endMs: +range.endDate,
      height: range.height,
      start: range.start,
      startMs: +range.startDate,
      top: range.top,
    };
  });

  const eventsInRenderOrder = sortDenseEventsForRender(proxies);
  const containerEvents: DenseLayoutProxy[] = [];

  eventsInRenderOrder.forEach(event => {
    const container = containerEvents.find(
      candidate =>
        candidate.end > event.start ||
        Math.abs(event.start - candidate.start) < minimumStartDifference,
    );

    if (!container) {
      event.rows = [];
      containerEvents.push(event);
      return;
    }

    event.container = container;
    const containerRows = container.rows ?? [];
    const row = [...containerRows].reverse().find(candidate =>
      areDenseEventsOnSameRow(candidate, event, minimumStartDifference),
    );

    if (row) {
      row.leaves?.push(event);
      event.row = row;
    } else {
      event.leaves = [];
      containerRows.push(event);
    }
  });

  return eventsInRenderOrder.map(event => {
    const xOffset = Math.max(0, getDenseProxyXOffset(event));
    return {
      event: event.data,
      style: {
        top: event.top,
        height: event.height,
        width: 100,
        xOffset: xOffset * STAGGER_COMPRESSION,
      } satisfies DenseEventStyle,
    };
  });
};
